import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { ArrowLeft, User, Mail, Phone, MapPin, Save, Bell, MessageCircle, Send, Check, X } from "lucide-react";
import { NestIcon } from "../components/NestIcon";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";

export default function Profile() {
  const { user, token, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: ""
  });

  // Telegram state
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [telegramChatId, setTelegramChatId] = useState("");
  const [linkingTelegram, setLinkingTelegram] = useState(false);
  const [testingNotification, setTestingNotification] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || "",
        phone: user.phone || "",
        address: user.address || "",
        city: user.city || "",
        state: user.state || "",
        zip_code: user.zip_code || ""
      });
    }
    fetchTelegramStatus();
  }, [user]);

  const fetchTelegramStatus = async () => {
    try {
      const response = await axios.get(`${API}/notifications/telegram/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTelegramStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch Telegram status");
    }
  };

  const handleLinkTelegram = async () => {
    if (!telegramChatId.trim()) {
      toast.error("Please enter your Telegram Chat ID");
      return;
    }

    setLinkingTelegram(true);
    try {
      await axios.post(`${API}/notifications/telegram/link`, 
        { telegram_chat_id: telegramChatId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Telegram linked successfully!");
      setTelegramChatId("");
      fetchTelegramStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link Telegram");
    } finally {
      setLinkingTelegram(false);
    }
  };

  const handleUnlinkTelegram = async () => {
    try {
      await axios.post(`${API}/notifications/telegram/unlink`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Telegram unlinked");
      fetchTelegramStatus();
    } catch (error) {
      toast.error("Failed to unlink Telegram");
    }
  };

  const handleTestNotification = async () => {
    setTestingNotification(true);
    try {
      await axios.post(`${API}/notifications/telegram/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Test notification sent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test notification");
    } finally {
      setTestingNotification(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.put(`${API}/users/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser({ ...user, ...response.data });
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <NestIcon className="h-8 w-8 text-primary" />
              <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Young Serif, serif' }}>
                TheRentPayer
              </span>
            </Link>
            <Link to="/dashboard">
              <Button variant="ghost">Back to Dashboard</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Young Serif, serif' }}>
            Your Profile
          </h1>
          <p className="text-muted-foreground">
            Manage your personal information
          </p>
        </div>

        <Card className="card-organic">
          <CardHeader>
            <div className="flex items-center gap-4">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.full_name} 
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-8 w-8 text-primary" />
                </div>
              )}
              <div>
                <CardTitle>{user?.full_name || "Your Name"}</CardTitle>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                {user?.account_number && (
                  <p className="text-xs text-primary font-mono mt-1">Account: {user.account_number}</p>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="full_name"
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="pl-10"
                      data-testid="profile-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="pl-10"
                      data-testid="profile-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address">Street Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="address"
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="pl-10"
                      data-testid="profile-address-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    data-testid="profile-city-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      data-testid="profile-state-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip_code">ZIP Code</Label>
                    <Input
                      id="zip_code"
                      type="text"
                      value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                      data-testid="profile-zip-input"
                    />
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                className="btn-primary w-full py-6"
                disabled={loading}
                data-testid="save-profile-btn"
              >
                <Save className="mr-2 h-4 w-4" />
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Telegram Notifications Card */}
        <Card className="card-organic mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-blue-500" />
              Telegram Notifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Get instant notifications via Telegram for claim updates, payment reminders, and policy alerts - completely free!
            </p>

            {telegramStatus?.user_linked ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 p-2 rounded-full">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-green-800">Telegram Connected</p>
                      <p className="text-sm text-green-600">Chat ID: {telegramStatus.chat_id}</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleUnlinkTelegram}
                    className="text-red-600 hover:bg-red-50"
                    data-testid="unlink-telegram-btn"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Unlink
                  </Button>
                </div>

                <Button 
                  variant="outline" 
                  onClick={handleTestNotification}
                  disabled={testingNotification}
                  className="w-full"
                  data-testid="test-telegram-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {testingNotification ? "Sending..." : "Send Test Notification"}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-800 mb-2">How to get your Chat ID:</p>
                  <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                    <li>Open Telegram and search for <strong>@userinfobot</strong></li>
                    <li>Send /start to the bot</li>
                    <li>Copy your Chat ID number</li>
                    <li>Paste it below and click Link</li>
                  </ol>
                </div>

                <div className="flex gap-2">
                  <Input
                    placeholder="Enter your Telegram Chat ID"
                    value={telegramChatId}
                    onChange={(e) => setTelegramChatId(e.target.value)}
                    className="flex-1"
                    data-testid="telegram-chat-id-input"
                  />
                  <Button 
                    onClick={handleLinkTelegram}
                    disabled={linkingTelegram}
                    data-testid="link-telegram-btn"
                  >
                    <MessageCircle className="h-4 w-4 mr-1" />
                    {linkingTelegram ? "Linking..." : "Link"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
